/**
 * Portal 会话鉴权：sessionStorage 中的 API Key、whoami、RBAC 写门禁、登录页。
 */
import { requestRuntime } from './api/runtime.js';

export const runtimeApiKeyStorageKey = 'pe-ai-runtime-api-key';

/** 当前会话 whoami（P1-14 RBAC 软门禁） */
let runtimeWhoami = { role: '', permissions: [] };

/** 写操作控件：viewer 只读时软禁用 */
export const RBAC_WRITE_SELECTORS = [
  '#parse-bp-btn',
  '#start-initial-review',
  '#bp-start-prejudge',
  '#prejudge-rerun',
  '#prejudge-confirm-review',
  '#ddq-generate-btn',
  '#ddq-skill-btn',
  '#valuation-skill-btn',
  '#icm-generate-btn',
  '#icm-p6-generate-btn',
  '#contract-skill-btn',
  '#contract-review-btn',
  '#contract-compare-btn',
  '#contract-consistency-btn',
  '#contract-supplement-btn',
  '#contract-history-btn',
  '#post-risk-analyze-btn',
  '#alert-closure-ack-btn',
  '#alert-closure-save-btn',
  '#kb-ask-btn',
  '#copilot-ask-btn',
  '[data-workflow]',
  '[data-resume-run]',
];

export function getRuntimeWhoami() {
  return runtimeWhoami;
}

export function getRuntimeApiKey() {
  return sessionStorage.getItem(runtimeApiKeyStorageKey)?.trim() || '';
}

export function canWriteBusiness() {
  const perms = runtimeWhoami.permissions || [];
  return perms.includes('business:write') || perms.includes('business:resume');
}

export function applyRbacUiGates() {
  const allowWrite = !runtimeWhoami.role || canWriteBusiness();
  for (const selector of RBAC_WRITE_SELECTORS) {
    document.querySelectorAll(selector).forEach((el) => {
      if (allowWrite) {
        el.classList.remove('rbac-write-disabled');
        if (el.tagName === 'BUTTON' || el.tagName === 'INPUT') {
          if (el.dataset.rbacDisabled === '1') {
            el.disabled = false;
            delete el.dataset.rbacDisabled;
          }
        }
        if (el.title && el.dataset.rbacTitle === '1') {
          el.removeAttribute('title');
          delete el.dataset.rbacTitle;
        }
      } else {
        el.classList.add('rbac-write-disabled');
        if ((el.tagName === 'BUTTON' || el.tagName === 'INPUT') && !el.disabled) {
          el.disabled = true;
          el.dataset.rbacDisabled = '1';
        }
        el.title = '当前角色为只读（viewer），无法执行写操作';
        el.dataset.rbacTitle = '1';
      }
    });
  }
  const banner = document.getElementById('rbac-banner');
  if (!banner) return;
  if (runtimeWhoami.role === 'viewer' || (runtimeWhoami.role && !canWriteBusiness())) {
    banner.textContent =
      `当前角色：${runtimeWhoami.role || 'unknown'}（只读）。可浏览项目与列表；写操作已禁用。更换 PE_AI_API_KEY / PE_AI_OPS_KEY 可恢复写入。`;
    banner.classList.add('is-visible');
  } else if (runtimeWhoami.role) {
    banner.textContent = `当前角色：${runtimeWhoami.role}`;
    banner.classList.toggle('is-visible', runtimeWhoami.role === 'viewer');
    if (runtimeWhoami.role !== 'viewer') {
      banner.classList.remove('is-visible');
      banner.textContent = '';
    }
  } else {
    banner.classList.remove('is-visible');
    banner.textContent = '';
  }
}

/** 代理未注入密钥且浏览器无 session 密钥时显示鉴权门禁 */
export function updateAuthGateBanner(options = {}) {
  const banner = document.getElementById('auth-gate-banner');
  if (!banner) return;
  const config = window.__PE_AI_PORTAL_CONFIG__ || {};
  const proxyInjected = config.proxyInjectedApiKey === true;
  const requireKey = config.requireRuntimeApiKey !== false;
  const hasSessionKey = Boolean(getRuntimeApiKey());
  const unauthorized = options.unauthorized === true;

  const show =
    !proxyInjected &&
    requireKey &&
    !hasSessionKey &&
    (unauthorized || options.forceShow === true);

  banner.classList.toggle('is-visible', show);
  banner.hidden = !show;

  const message = document.getElementById('auth-gate-message');
  if (message) {
    message.textContent = unauthorized
      ? 'Runtime 鉴权失败：API 密钥无效或未配置。请重新登录。'
      : 'Runtime 需要 API 密钥才能访问。Portal 代理未注入密钥，请在浏览器中登录。';
  }
}

export function openAuthLoginDialog() {
  const dialog = document.getElementById('auth-login-dialog');
  const input = document.getElementById('auth-login-api-key');
  if (!dialog || !input) {
    return promptForRuntimeApiKey();
  }
  input.value = getRuntimeApiKey();
  if (typeof dialog.showModal === 'function') {
    dialog.showModal();
  } else {
    return promptForRuntimeApiKey();
  }
  return true;
}

function bindAuthLoginDialog() {
  const dialog = document.getElementById('auth-login-dialog');
  const form = document.getElementById('auth-login-form');
  const cancel = document.getElementById('auth-login-cancel');
  if (!dialog || !form) return;

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    const input = document.getElementById('auth-login-api-key');
    const value = input instanceof HTMLInputElement ? input.value : '';
    saveRuntimeApiKey(value);
    dialog.close();
    updateAuthGateBanner({ forceShow: false });
    window.location.reload();
  });

  cancel?.addEventListener('click', () => dialog.close());
  document.getElementById('auth-gate-login-btn')?.addEventListener('click', () => {
    openAuthLoginDialog();
  });
}

export async function refreshRuntimeWhoami() {
  if (!getRuntimeApiKey() && !window.__PE_AI_PORTAL_CONFIG__?.proxyInjectedApiKey) {
    runtimeWhoami = { role: '', permissions: [] };
    applyRbacUiGates();
    updateAuthGateBanner({ forceShow: true });
    return null;
  }
  try {
    const me = await requestRuntime('/api/business/me', {}, false, true);
    runtimeWhoami = {
      role: me.role || '',
      permissions: Array.isArray(me.permissions) ? me.permissions : [],
    };
    applyRbacUiGates();
    updateAuthGateBanner({ unauthorized: false });
    return runtimeWhoami;
  } catch {
    runtimeWhoami = { role: '', permissions: [] };
    applyRbacUiGates();
    updateAuthGateBanner({ unauthorized: true });
    return null;
  }
}

export function saveRuntimeApiKey(apiKey) {
  const value = apiKey.trim();
  if (value) {
    sessionStorage.setItem(runtimeApiKeyStorageKey, value);
  } else {
    sessionStorage.removeItem(runtimeApiKeyStorageKey);
  }
  updateRuntimeKeyButton();
  void refreshRuntimeWhoami();
}

export function updateRuntimeKeyButton() {
  const button = document.getElementById('runtime-key-button');
  if (button) button.textContent = getRuntimeApiKey() ? 'API 密钥已设置' : 'API 密钥';
}

export function promptForRuntimeApiKey(message) {
  const opened = openAuthLoginDialog();
  if (opened) return Boolean(getRuntimeApiKey());
  const value = window.prompt(
    message ||
      'Runtime 需要鉴权。请输入与 PE_AI_API_KEY / PE_AI_OPS_KEY / PE_AI_VIEWER_KEY 相同的 API 密钥；密钥仅保存在当前浏览器会话中。',
    getRuntimeApiKey(),
  );
  if (value === null) return false;
  saveRuntimeApiKey(value);
  return Boolean(value.trim());
}

/** 绑定情报中心外链与 API 密钥按钮 */
export function initAuthChrome() {
  const centerUrl = window.__PE_AI_PORTAL_CONFIG__?.intelligenceCenterUrl;
  if (centerUrl) {
    document.getElementById('intelligence-center-link')?.setAttribute('href', centerUrl);
    document
      .querySelector('.nav-item[data-external-href]')
      ?.setAttribute('data-external-href', centerUrl);
  }
  document.getElementById('runtime-key-button')?.addEventListener('click', () => {
    openAuthLoginDialog();
  });
  bindAuthLoginDialog();
  updateRuntimeKeyButton();
  updateAuthGateBanner({ forceShow: true });
}
